import {useState, VoidFunctionComponent} from "react";
import React, {useEffect, useRef} from "react";
import s from "./SectionEndAward.module.scss";
import {SectionAwardIcon} from "./sectionAwardIcon/SectionAwardIcon";
import ribbon from "../../../bootCamp/CourseSection/CourseSectionBody/unlocked-songs-ribbon.png";
import Button from "../../../common/Button";
import {navigate} from "../../../../services/navigation/navigation";
import {Swiper as SwiperCore} from "swiper";
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination} from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import deviceInfo from "../../../../services/deviceInfo/deviceInfo";

export interface ISectionEndAwardProps {
  sectionTitle: string;
  sectionNumber: number;
  nextSectionTitle?: string;
  sectionId: number;
  hasUnlockedSongs: boolean;
  closeTheModal: () => void;
}

export const SectionEndAward: VoidFunctionComponent<ISectionEndAwardProps> = ({
  sectionTitle,
  sectionNumber,
  nextSectionTitle,
  sectionId,
  hasUnlockedSongs,
  closeTheModal,
}) => {
  const completionMsgRef = useRef<HTMLHeadingElement>(null);
  const awardIconRef = useRef<HTMLDivElement>(null);
  const sectionTitleOverlineRef = useRef<HTMLParagraphElement>(null);
  const figCaptionRef = useRef<HTMLElement>(null);
  const nextSectionTitleOverlineRef = useRef<HTMLParagraphElement>(null);
  const nextSectionTitleRef = useRef<HTMLHeadingElement>(null);
  const unlockedSongsRef = useRef<HTMLDivElement>(null);
  const sectionEndAwardRef = useRef<HTMLDivElement>(null);
  const [swiper, setSwiper] = useState<SwiperCore | null>(null);
  const isSlideshow = deviceInfo.getIsMobile();

  useEffect(() => {
    if (isSlideshow && !swiper) {
      // Wait until swiper is loaded to start the animation
      return;
    }

    awardIconRef?.current?.setAttribute('style', 'opacity: 0');
    sectionTitleOverlineRef?.current?.setAttribute('style', 'opacity: 0');
    figCaptionRef?.current?.setAttribute('style', 'opacity: 0');
    nextSectionTitleOverlineRef?.current?.setAttribute('style', 'opacity: 0');
    nextSectionTitleRef?.current?.setAttribute('style', 'opacity: 0');

    completionMsgRef?.current?.animate(
      [
        {opacity: 0, transform: `scale(0.7)`, easing: 'ease-out'},
        {opacity: 1, transform: `scale(1.15)`, filter: 'blur(0)', offset: 0.75},
        {opacity: 0, transform: `scale(1)`, filter: 'blur(10px)', easing: 'ease-in'},
      ], {
        duration: 2000,
        fill: 'forwards',
      }
    ).finished.then(() => {
      awardIconRef?.current?.animate([
        {opacity: 0, transform: `scale(0.25)`, filter: 'blur(10px)', easing: 'cubic-bezier(0.33, 0.98, 0, 0.83)'},
        {opacity: 1, transform: `scale(1.25)`, filter: 'blur(0)'},
        {opacity: 1, transform: `scale(1)`},
      ], {
        duration: 750,
        fill: 'forwards',
      }
      ).finished.then(() => {
        sectionTitleOverlineRef?.current?.animate(
          [
            {opacity: 0, transform: `scale(0.75)`, easing: 'ease-out'},
            {opacity: 1, transform: `scale(1.15)`, offset: 0.5},
            {opacity: 1, transform: `scale(1)`, easing: 'ease-in'},
          ], {
            duration: 250,
            fill: 'forwards',
          }
        ).finished.then(() => {
          figCaptionRef?.current?.animate([
            {opacity: 0, transform: `scale(0.75)`, easing: 'ease-out'},
            {opacity: 1, transform: `scale(1.15)`, offset: 0.5},
            {opacity: 1, transform: `scale(1)`, easing: 'ease-in'},
          ], {
            duration: 250,
            endDelay: 250,
            fill: 'forwards',
          }
          ).finished.then(() => {
            nextSectionTitleOverlineRef?.current?.animate([
              {opacity: 0, transform: `scale(0.75)`, easing: 'ease-out'},
              {opacity: 1, transform: `scale(1.15)`, offset: 0.5},
              {opacity: 1, transform: `scale(1)`, easing: 'ease-in'},
            ], {
              duration: 250,
              fill: 'forwards',
            }
            ).finished.then(() => {
              nextSectionTitleRef?.current?.animate([
                {opacity: 0, transform: `scale(0.75)`, easing: 'ease-out'},
                {opacity: 1, transform: `scale(1.15)`, offset: 0.5},
                {opacity: 1, transform: `scale(1)`, easing: 'ease-in'},
              ], {
                duration: 250,
                endDelay: 800,
                fill: 'forwards',
              }
              ).finished.then(() => {
                if (!hasUnlockedSongs) {
                  // Don't show the unlocked songs animation if there are no songs to unlock!
                  return;
                }

                if (isSlideshow) {
                  // Mobile animation
                  swiper?.slideNext();
                  return;
                }

                // Desktop animation
                unlockedSongsRef?.current?.animate([
                  {left: `100%`},
                  {left: `75%`, offset: 0.5},
                  {left: `50%`},
                ], {
                  duration: 250,
                  fill: 'forwards',
                });
                sectionEndAwardRef?.current?.animate([
                  {width: `100%`},
                  {width: `75%`, offset: 0.5},
                  {width: `50%`},
                ],
                {
                  duration: 250,
                  fill: 'forwards',
                })
              })
            })
          })
        })
      })
    })
  }, [isSlideshow, swiper, hasUnlockedSongs]);

  function createTrophySlide() {
    return (
      <div ref={sectionEndAwardRef} className={s.sectionEndAward}>
        <h1 ref={completionMsgRef} className={s.completionMessage}>Congratulations!</h1>
        <p ref={sectionTitleOverlineRef} className={s.sectionTitleOverline}>
        Section completed!
        </p>
        <figure>
          <figcaption ref={figCaptionRef}>{sectionTitle}</figcaption>
          <div ref={awardIconRef} className={s.awardIconWrapper}>
            <SectionAwardIcon sectionNumber={sectionNumber}/>
          </div>
        </figure>
        <p ref={nextSectionTitleOverlineRef} className={s.nextSectionTitleOverline}>
        Next section
        </p>
        <h2 ref={nextSectionTitleRef}>{nextSectionTitle}</h2>
      </div>
    );
  }

  function createRibbonSlide() {
    if (!hasUnlockedSongs) {
      return null;
    }

    const onSeeSongsButtonClick = () => {
      navigate(`/unlocked-song-lessons/${sectionId}`);
      closeTheModal();
    }

    return <div ref={unlockedSongsRef} className={s.bonus}>
      <div className={s.bonusContent}>
        <div className={s.ovalAndRibbon}>
          <div className={s.oval}></div>
          <div className={s.ribbon}>
            <img src={ribbon} alt="Bonus award ribbon" width={"100%"}/>
          </div>
        </div>
        <div className={s.textAndButton}>
          <div className={s.text}>
            Congratulations!<br/>
            Bonus songs unlocked
          </div>
          <Button text={"See Songs"} additionalClassName={s.seeSongsAction}
            onClick={onSeeSongsButtonClick}/>
        </div>
      </div>
    </div>;
  }

  function createSwiperSlideshow() {
    return <Swiper pagination={{
      bulletActiveClass: `pgSwiperActiveBullet`,
    }} modules={[Pagination]} onSwiper={(s) => setSwiper(s)}>
      <SwiperSlide>
        {createTrophySlide()}
      </SwiperSlide>
      {hasUnlockedSongs
        ? <SwiperSlide>
          {createRibbonSlide()}
        </SwiperSlide>
        : null}
    </Swiper>;
  }

  return (
    <div className={s.slideInLayout}>
      {isSlideshow
        ? createSwiperSlideshow()
        : <>{createTrophySlide()}{createRibbonSlide()}</>
      }
    </div>
  );
}
